import { useState } from "react";
import { Copy, Check } from "lucide-react";

interface Props {
  templateName: string;
  availableTemplates: string[];
  onRetry?: () => void;
}

export function MissingTemplate({
  templateName,
  availableTemplates,
  onRetry,
}: Props) {
  const [copied, setCopied] = useState(false);

  const expectedPath = `app/frontend/_pages/${templateName}.tsx`;

  const formatDebugInfo = () => {
    return [
      "## Missing Inertia Template",
      "",
      "### Missing Template",
      `\`${templateName}.tsx\``,
      "",
      "### Expected Path",
      `\`${expectedPath}\``,
      "",
      "### Available Templates",
      ...availableTemplates.map((name) => `- ${name}`),
      "",
      "### Environment",
      `- URL: ${window.location.href}`,
      `- Timestamp: ${new Date().toISOString()}`,
      `- User Agent: ${navigator.userAgent}`,
      "",
      "### Suggested Actions",
      "1. Create the missing template file",
      "2. Verify controller render(inertia: ...) component name",
      "3. Confirm file path and naming convention",
    ].join("\n");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formatDebugInfo());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#f8f7f2] to-[#eef3f0] text-gray-900">
      <div className="mx-auto flex min-h-screen max-w-2xl items-center px-6 py-16">
        <section className="w-full rounded-3xl border border-gray-200/80 bg-white/90 p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
            Error 500
          </p>
          <h1 className="mt-3 text-2xl font-semibold leading-tight text-gray-900 sm:text-3xl">
            Missing Inertia Template
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
            テンプレートが見つかりません。component
            名とファイルパスを確認してください。
          </p>

          <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 text-xs text-gray-700">
            <div>
              <span className="text-gray-500">Template:</span> {templateName}
              .tsx
            </div>
            <div className="mt-1 break-all">
              <span className="text-gray-500">Expected:</span> {expectedPath}
            </div>
          </div>

          <details className="mt-6 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 text-xs text-gray-700">
            <summary className="cursor-pointer text-sm font-semibold text-gray-800">
              詳細を表示
            </summary>
            <div className="mt-3 space-y-3">
              <div className="flex justify-end gap-2">
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                  >
                    再試行
                  </button>
                )}
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-green-600" />
                      コピー完了
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      /llms.txt 形式でコピー
                    </>
                  )}
                </button>
              </div>

              {availableTemplates.length > 0 && (
                <div>
                  <p className="mb-2 text-gray-500">Available Templates:</p>
                  <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-gray-600">
                    {availableTemplates.join("\n")}
                  </pre>
                </div>
              )}
            </div>
          </details>
        </section>
      </div>
    </main>
  );
}
